// src/components/ImgHeader.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // ✅ Añadir useNavigate
import TrackingSearch from './TrackingSearch';
import AuthForm from './AuthForm';
import TrackingResults from './TrackingResults';
import { useTracking } from '../hooks/useTracking';
import trackingService from '../services/trackingService';

const ImgHeader = ({
  loading,
  error,
  trackingData,
  currentStep,
  authType,
  pendingTrackingNumber,
  preAuthData,
  loadingMessage,
  searchTracking,
  requestAuth,
  resetTracking,
  resetForNewSearch, // ✅ Recibir nueva función
  setCurrentStep,
  setTrackingData,
}) => {
  const [apiStatus, setApiStatus] = useState('checking');
  const [showError, setShowError] = useState(false);
  const navigate = useNavigate(); // ✅ Hook para navegación

  // ✅ Efecto para manejar la visibilidad del error
  useEffect(() => {
    if (error) {
      // Mostrar el error inmediatamente
      setShowError(true);

      // Configurar temporizador para ocultar después de 5 segundos
      const timer = setTimeout(() => {
        setShowError(false);
      }, 5000);

      // Limpiar el temporizador si el componente se desmonta o cambia el error
      return () => clearTimeout(timer);
    } else {
      // Si no hay error, ocultar
      setShowError(false);
    }
  }, [error]); // Se ejecuta cuando cambia el error

  // Efecto para el check de API (mantener existente)
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        await trackingService.healthCheck();
        setApiStatus('online');
      } catch (error) {
        setApiStatus('offline');
        console.error('API no disponible:', error);
      }
    };

    checkApiStatus();
  }, []);

  // En ImgHeader.jsx - buscar esta función y actualizarla
  const handleInitialSearch = (trackingNumber) => {
    if (!loading && trackingNumber !== pendingTrackingNumber) {
      const urlNumber = trackingNumber.startsWith('#')
        ? trackingNumber.substring(1)
        : trackingNumber;

      navigate(`/tracking/${urlNumber}`);
    }
  };

  const saveAuthData = (trackingNumber, authValue, preAuthData) => {
    if (trackingNumber && preAuthData) {
      try {
        const authData = {
          authValue,
          preAuthData: preAuthData,
          timestamp: new Date().getTime(),
          expiresAt: new Date().getTime() + 60 * 60 * 1000, // 60 minutos
        };

        localStorage.setItem(
          `tracking_${trackingNumber}`,
          JSON.stringify(authData)
        );
        console.log('✅ Datos de autenticación guardados en localStorage');
      } catch (error) {
        console.error('Error saving auth data:', error);
      }
    }
  };

  const handleAuthSubmit = (authValue) => {
    console.log('🔐 Enviando autenticación:', {
      trackingNumber: pendingTrackingNumber,
      authValue: authValue,
      preAuthData: preAuthData,
    });

    // ✅ ACTUALIZADO: Guardar datos de autenticación
    saveAuthData(pendingTrackingNumber, authValue, preAuthData);

    searchTracking(authValue);
  };

  const handleBackToSearch = () => {
    resetTracking();
    navigate('/tracking'); // ✅ Redirigir a la ruta base
  };

  // En ImgHeader.jsx - Actualizar handleNewSearch
  const handleNewSearch = () => {
    // ✅ Marcar que acabamos de salir de resultados
    localStorage.setItem('just_left_results', 'true');

    // ✅ Reset visual
    resetTracking();

    // ✅ Navegar
    navigate('/tracking', { replace: true });
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'search':
        return 'Rastrea tu pedido en tiempo real. Ingresa tu número de seguimiento para comenzar.';
      case 'auth':
        if (preAuthData && !preAuthData.requires_auth) {
          return 'Tu pedido no requiere autenticación adicional. Obteniendo información...';
        }
        return authType === 'zipcode'
          ? 'Para proteger tu información, ingresa el código postal de la dirección de entrega.'
          : 'Para proteger tu información, ingresa el RFC de la empresa que realizó el pedido.';
      case 'results':
        return 'Información detallada del estado de tu pedido.';
      default:
        return 'Sistema de seguimiento de pedidos Packsys.';
    }
  };

  // Componente de Loading Mejorado
  const LoadingSpinner = ({ message = 'Cargando...' }) => (
    <div className="w-full max-w-md mx-auto bg-white/10 backdrop-blur-lg rounded-2xl p-8 border-2 border-white/20">
      <div className="flex flex-col items-center justify-center">
        <div className="relative">
          {/* Spinner principal */}
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4"></div>
        </div>

        <p className="text-white text-center font-medium mb-2">{message}</p>

        {/* Puntos animados */}
        <div className="flex space-x-1">
          <div
            className="w-2 h-2 bg-packsys-orange rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-packsys-orange rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-packsys-orange rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          ></div>
        </div>

        {/* Texto de estado */}
        <p className="text-white/70 text-sm text-center mt-3">
          {message.includes('Verificando') &&
            'Esto puede tomar unos segundos...'}
          {message.includes('Obteniendo') &&
            'Recopilando información detallada...'}
          {message.includes('credenciales') && 'Validando y cargando datos...'}
        </p>
      </div>
    </div>
  );

  // Componente de Éxito (transición)
  const SuccessTransition = ({ onContinue }) => (
    <div className="w-full max-w-md mx-auto bg-green-500/20 backdrop-blur-lg rounded-2xl p-8 border-2 border-green-400/30 animate-pulse">
      <div className="flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 animate-scale-in">
          <svg
            className="w-8 h-8 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h3 className="text-white text-xl font-bold mb-2 text-center">
          ¡Información encontrada!
        </h3>
        <p className="text-green-100 text-center mb-4">
          Cargando detalles del pedido...
        </p>

        <button
          onClick={onContinue}
          className="bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors"
        >
          Continuar
        </button>
      </div>
    </div>
  );

  return (
    <section className="relative w-full min-h-screen bg-center bg-no-repeat bg-cover bg-[url('https://cdn.shopify.com/s/files/1/0399/8685/6102/files/DSC_08961.jpg?v=1697039409')] bg-packsys-blue bg-blend-multiply mt-24">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-800/20 to-blue-500/30"></div>

      {apiStatus === 'offline' && (
        <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg text-sm animate-pulse">
          ⚠️ API no disponible
        </div>
      )}

      <div className="relative px-4 mx-auto max-w-screen-xl text-center py-24 lg:py-32">
        <div className="animate-fade-in-up">
          <h1 className="mb-4 md:mb-6 text-5xl font-black tracking-tight leading-none text-white md:text-6xl lg:text-7xl animate-slide-up transform transition-all duration-1000">
            Tracking de <span className="text-packsys-orange">Pedidos</span>
          </h1>

          <p className="mb-8 md:mb-12 text-lg font-light text-gray-200 md:text-xl lg:text-2xl px-4 md:px-16 lg:px-48 max-w-5xl mx-auto leading-relaxed animate-slide-up animation-delay-300 transform transition-all duration-1000">
            {getStepDescription()}
          </p>

          <div className="animate-slide-up animation-delay-600 transform transition-all duration-1000">
            {/* ✅ Loading State */}
            {loading && <LoadingSpinner message={loadingMessage} />}

            {/* ✅ Estados normales cuando no está loading */}
            {!loading && currentStep === 'search' && (
              <TrackingSearch
                onSearch={handleInitialSearch}
                onRequestAuth={requestAuth}
                loading={loading}
              />
            )}

            {!loading &&
              currentStep === 'auth' &&
              preAuthData?.requires_auth && (
                <AuthForm
                  trackingNumber={pendingTrackingNumber}
                  authType={authType}
                  onSubmit={handleAuthSubmit}
                  onBack={handleBackToSearch}
                  loading={loading}
                />
              )}

            {!loading && currentStep === 'results' && trackingData && (
              <TrackingResults
                data={trackingData}
                onNewSearch={handleNewSearch}
              />
            )}

            {/* ✅ Estado de éxito (puedes usarlo como transición) */}
            {!loading && currentStep === 'results' && !trackingData && (
              <SuccessTransition onContinue={handleNewSearch} />
            )}

            {showError && error && (
              <div className="w-full max-w-md mx-auto bg-red-50 border border-red-200 rounded-2xl p-6 mt-6 animate-shake relative">
                {/* Botón para cerrar manualmente (opcional) */}
                <button
                  onClick={() => setShowError(false)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  aria-label="Cerrar mensaje de error"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                <div className="flex items-center mb-3">
                  <svg
                    className="w-6 h-6 text-red-500 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-700 font-medium">Error</p>
                </div>
                <p className="text-red-600 text-sm mb-4">{error}</p>

                {/* ✅ Indicador de tiempo (opcional) */}
                <div className="w-full bg-red-200 rounded-full h-1.5">
                  <div
                    className="bg-red-500 h-1.5 rounded-full animate-countdown"
                    style={{
                      animation: 'countdown 5s linear forwards',
                      animationPlayState: 'running',
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {currentStep === 'search' && !loading && (
            <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto px-4 sm:px-0 animate-fade-in animation-delay-900">
              {[
                {
                  icon: '🚚',
                  title: 'Envío de Calidad',
                  desc: 'Entregas eficaces en todo el país',
                },
                {
                  icon: '📱',
                  title: 'Seguimiento en Tiempo Real',
                  desc: 'Actualizaciones instantáneas de tu pedido',
                },
                {
                  icon: '🔒',
                  title: 'Seguro y Confiable',
                  desc: 'Tu pedido en las mejores manos',
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 md:p-6 lg:p-8 border-2 border-white/20 hover:border-white/40 transition-all duration-500 hover:bg-white/15 hover:shadow-2xl"
                >
                  <div className="text-3xl md:text-4xl mb-3 md:mb-4 transform transition-all duration-500 hover:scale-110">
                    {feature.icon}
                  </div>
                  <h3 className="text-white font-bold text-lg md:text-xl lg:text-xl mb-2 md:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-200 text-sm md:text-base leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ImgHeader;
